import * as React from "react";
import * as S from "@styled";
import * as _ from "lodash"
import * as AppPath from "../model/AppPath";
import { connect } from "react-redux";
import { HashMap } from "../../shared/model/HashMap";
import { AgentProcessModel } from "../model/AgentProcessModel";
import { State } from "../model/State";
import CustomPopover from "shared/components/Popover";
import { FilterCategory, ListState } from "processList/model/ListState";
import Time from "shared/Time";

const TriggerDisplay: React.FC<TriggerDisplayProps> = (props) => {

    const agentStatusValues = _.values(props.agentStatuses);

    const allAgentsActive = !_.isEmpty(agentStatusValues) && _.every(agentStatusValues, (as) => as.isActive);
    const hasErrors = _.some(agentStatusValues, (status) => status.initializationError);
    const hasWrongVersion = _.some(agentStatusValues, (status) => !status.isRightVersion);
    const someAgentsOfflineWithTriggers = !_.isEmpty(props.agentStatuses) && _.some(agentStatusValues, (as) => !as.isActive);

    const getTriggerDisplayName = (triggerType: string) => {
        switch (triggerType) {
            case "FileWatchTrigger":
                return "FILE";
            case "HttpApiTrigger":
                return "API";
            default:
                return triggerType.replace("Trigger", "").toUpperCase();
        }
    };

    const getAgentStatus = () => {
        return _.map(Object.keys(props.agentStatuses as HashMap<AgentTriggerStatus>), (a) => {
            if (props.agentStatuses !== undefined) {
                const status = props.agentStatuses[a];
                if (status.initializationError) {
                    return <span key={a + "_initError"}>{a}: {status.initializationError}</span>;
                }
                if (status.isExpired) {
                    return <span key={a + "_agentOffline"}>{a}: {FRENDS.Localizations.Agent_IsOffline} since {Time.toDetailedLocalTime(status.agentOfflineSince)}</span>;
                }
                if (status.isActive && !status.isRightVersion) {
                    return <span key={a + "_wrongVersion"}>{a}: {FRENDS.Localizations.API_ExpectedVersion} {props.processVersion}, {FRENDS.Localizations.API_FoundVersion.toLowerCase()} {status.agentVersion}</span>;
                }
                return <span key={a + "_agentStatus"}>{a}: {status.isActive ? FRENDS.Localizations.IsActive : FRENDS.Localizations.NotActive}</span>;
            }
        })
    };

    const tooltip = (
        <S.FlexContainer asColumn={true} id={"status_" + props.processId}>
            <span>{props.triggerDescription}</span>
            {getAgentStatus()}
        </S.FlexContainer>
    );

    const getTriggerVariant = (): S.ProcessTriggerChipVariant => {
        if (hasErrors || someAgentsOfflineWithTriggers) {
            return S.ProcessTriggerChipVariant.error;
        }
        if (!allAgentsActive) {
            return S.ProcessTriggerChipVariant.deactivated;
        }
        if (!hasErrors && hasWrongVersion && allAgentsActive) {
            return S.ProcessTriggerChipVariant.warning;
        }
        return S.ProcessTriggerChipVariant.active;
    }

    const handleClick = () => {
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({
            filterCategory: FilterCategory.TriggerType,
            filterText: props.triggerType,
            page: null
        }));
    };

    return (
        <CustomPopover
            tooltip={tooltip}
            clickHandlerStyles={{ cursor: "pointer", margin: "8px 0px" }}
            useHover={true}
        >
            <S.ProcessTriggerChip variant={agentStatusValues.length > 0 ? getTriggerVariant() : undefined} onClick={handleClick} className={`trigger-${getTriggerDisplayName(props.triggerType).toLowerCase()}-selector`}>
                {getTriggerDisplayName(props.triggerType)}
            </S.ProcessTriggerChip>
        </CustomPopover>
    );
};


export interface TriggerDisplayOwnProps {
    processId: string;
    processVersion: number;
    processActive: boolean;
    triggerType: string;
    triggerName: string;
    triggerDescription: string;
}

export interface TriggerDisplayStateProps {
    agentStatuses: HashMap<AgentTriggerStatus>;
    listState: ListState;
}

interface AgentTriggerStatus {
    isExpired: boolean;
    isActive: boolean;
    isRightVersion: boolean;
    agentVersion: number;
    initializationError: string;
    agentOfflineSince: string;
}

type TriggerDisplayProps = TriggerDisplayOwnProps & TriggerDisplayStateProps;

const mapStateToProps = (state: State, ownProps: TriggerDisplayOwnProps): TriggerDisplayStateProps => {
    const minTimestampToUse = Time.add(new Date(), { minutes: -35 }).toISOString();
    const agentStatuses: HashMap<AgentTriggerStatus> = {};
    for (const agentKey of Object.keys(state.agentStatuses)) {
        const agentStatus = state.agentStatuses[agentKey];
        const skipTrigger = agentStatus.triggerTypesNotInUse != null &&
            agentStatus.triggerTypesNotInUse.some(
                (triggerType) => triggerType.toLowerCase() === ownProps.triggerType.toLowerCase());

        if (skipTrigger) {
            continue;
        }

        const isExpired = agentStatus.timestamp < minTimestampToUse;
        const processStatus = !isExpired && agentStatus.processInfos[ownProps.processId] || {} as AgentProcessModel;
        const hasException = _.has(processStatus, `initializationExceptions["${ownProps.triggerName}"]`);

        agentStatuses[agentKey] = {
            isExpired: isExpired,
            isActive: processStatus != null
                && processStatus.triggersActive === true
                && !hasException
                && processStatus.triggers.indexOf(ownProps.triggerName) !== -1,
            isRightVersion: processStatus != null && ownProps.processVersion === processStatus.version,
            agentVersion: processStatus.version,
            initializationError: hasException ? processStatus.initializationExceptions[ownProps.triggerName] : "",
            agentOfflineSince: agentStatus.timestamp
        };
    }

    return {
        agentStatuses: agentStatuses,
        listState: state.listState
    };
};

export default connect(mapStateToProps)(TriggerDisplay);
